import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { preloadCriticalAuthNamespaces, preloadDeferredAuthNamespaces } from "../i18n";
import { setReturnUrl } from "../utils/urlUtils";
import { usePendingPollClaim } from "../hooks/usePendingPollClaim";
import LoadingSpinner from "./core/LoadingSpinner";

interface PrivateRouteProps {
  children: React.ReactNode;
  skipOnboardingCheck?: boolean; // For the onboarding route itself
}

const PrivateRoute: React.FC<PrivateRouteProps> = ({
  children,
  skipOnboardingCheck = false
}) => {
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();

  // Finishes a poll someone started moving into an account before they had
  // one. Here because it is the single component every authenticated page
  // renders through, and the claim must complete wherever signup happens to
  // land them. No-op when nothing is parked.
  usePendingPollClaim(isAuthenticated);

  // i18n preloading for authenticated routes:
  //   1. Critical namespaces (chrome + schedule cards) — eager so JA users don't see EN fallback flash.
  //   2. Remaining namespaces (other sections) — deferred to requestIdleCallback so they don't
  //      compete with the API fan-out for sockets / main-thread time during the critical first paint.
  React.useEffect(() => {
    if (!isAuthenticated) return;
    preloadCriticalAuthNamespaces();
    const idle: (cb: () => void) => number =
      (window as any).requestIdleCallback || ((cb: () => void) => window.setTimeout(cb, 200));
    const handle = idle(() => preloadDeferredAuthNamespaces());
    return () => {
      const cancel = (window as any).cancelIdleCallback;
      if (cancel) cancel(handle);
      else window.clearTimeout(handle);
    };
  }, [isAuthenticated]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" color="primary" />
      </div>
    );
  }

  // Not authenticated - remember where they were headed, then redirect to login.
  // Deep links from email CTAs (e.g. "Upgrade Now" → /profile?tab=plans) land here
  // whenever the session has lapsed; without this the destination is lost and
  // sign-in dumps the user on /schedule.
  if (!isAuthenticated) {
    setReturnUrl(`${location.pathname}${location.search}`);
    return <Navigate to="/login" replace />;
  }

  // If on onboarding route but already completed onboarding - redirect to schedule
  // This prevents users from accessing onboarding after they've completed it
  if (skipOnboardingCheck && user && user.onboardingCompleted === true) {
    return <Navigate to="/schedule" replace />;
  }

  // Authenticated but onboarding not completed - redirect to onboarding
  // UNLESS we're already on the onboarding route or skipOnboardingCheck is true
  if (!skipOnboardingCheck && user && user.onboardingCompleted === false) {
    return <Navigate to="/onboarding" replace />;
  }

  return <>{children}</>;
};

export default PrivateRoute;
